import { Box, BoxProps } from '@chakra-ui/react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useState } from 'react';

/**
 * Generic interface for items that can be reordered
 * Must have a unique id property
 */
interface ReorderableItem {
  id: string;
}

/**
 * Props for the ReorderGrid component
 * @template T - Type of items being reordered (must extend ReorderableItem)
 */
interface ReorderGridProps<T extends ReorderableItem> {
  values: T[];
  onReorder: (newOrder: T[]) => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Props for the ReorderItem component
 * @template T - Type of item being rendered (must extend ReorderableItem)
 */
interface ReorderItemProps<T extends ReorderableItem> {
  value: T;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  dragControls?: boolean;
}

/**
 * A grid component that enables drag-and-drop reordering of its children
 * @template T - Type of items being reordered (must extend ReorderableItem)
 */
function ReorderGrid<T extends ReorderableItem>({
  values,
  onReorder,
  children,
  className,
  style,
  ...props
}: ReorderGridProps<T> & BoxProps) {
  const [activeId, setActiveId] = useState<T['id'] | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 }, // Prevent accidental drags
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 }, // Better mobile experience
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as T['id']);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      try {
        const oldIndex = values.findIndex((item) => item.id === active.id);
        const newIndex = values.findIndex((item) => item.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
          // eslint-disable-next-line no-console
          console.error('Invalid drag operation: item not found');
          return;
        }

        onReorder(arrayMove(values, oldIndex, newIndex));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error during reordering:', error);
      }
    }

    setActiveId(null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={values} strategy={rectSortingStrategy}>
        <Box className={className} style={style} {...props}>
          {children}
        </Box>
      </SortableContext>

      <DragOverlay adjustScale>
        {activeId && (
          <div
            className={className}
            style={{
              ...style,
              opacity: 0.8,
              transform: 'rotate(-4deg)',
              cursor: 'grabbing',
              pointerEvents: 'none',
            }}
          >
            {React.Children.toArray(children).find(
              (_, index) => values[index]?.id === activeId,
            )}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * A draggable item component to be used within ReorderGrid
 * @template T - Type of item being rendered (must extend ReorderableItem)
 */
function ReorderItem<T extends ReorderableItem>({
  value,
  children,
  className,
  style,
  dragControls,
  ...props
}: ReorderItemProps<T> & BoxProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: value.id });

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
    opacity: isDragging ? 0 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      className={className}
      style={{
        position: 'relative',
        ...sortableStyle,
        ...style,
      }}
      {...attributes}
      {...props}
      {...(!dragControls && { ...listeners })}
    >
      {children}
    </Box>
  );
}

export type { ReorderableItem, ReorderGridProps, ReorderItemProps };
export { ReorderGrid, ReorderItem };
